import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';

import { SousCategorie } from 'app/models/sous-categorie';
import { ProduitsService } from 'app/services/produits/produits.service';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

interface PendingImage {
  file: File;
  safeUrl: SafeUrl;
  uploadedUrl?: string;
}

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-ajouter-sous-categorie',
  templateUrl: './ajouter-sous-categorie.component.html',
  styleUrls: ['./ajouter-sous-categorie.component.scss']
})
export class AjouterSousCategorieComponent implements OnInit {
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  sousCategorie: SousCategorie = new SousCategorie();
  produit: any;
  idProduit: string | null = null;

  // ✅ Language support
  agencyAvailableLanguages: string[] = [];
  showFrenchFields: boolean = false;
  showArabicFields: boolean = false;
  showEnglishFields: boolean = false;
  languageLabels: { [code: string]: string } = { 'FR': 'Français', 'AR': 'العربية', 'EN': 'English' };

  // ============================================================
  // ✅ RÉFÉRENCES AUX ÉDITEURS contenteditable (WYSIWYG)
  // ============================================================
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;
  @ViewChild('descArEditor') descArEditor?: ElementRef<HTMLElement>;
  @ViewChild('descEnEditor') descEnEditor?: ElementRef<HTMLElement>;
  // ============================================================

  // ==================== MAIN IMAGE ====================
  mainImage: PendingImage | null = null;

  mainUploadMode: ImageUploadMode = 'direct';
  mainPreviewOriginalUrl: SafeUrl | string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  // ==================== SECONDARY IMAGES (galerie) ====================
  secondaryImages: PendingImage[] = [];

  secondaryUploadMode: ImageUploadMode = 'direct';
  secondaryPreviewOriginalUrls: (SafeUrl | string)[] = [];
  secondaryPreviewProcessedUrls: string[] = [];
  isAwaitingSecondaryValidation: boolean = false;

  uploadingMain = false;
  uploadingSecondary = false;
  isSubmitting = false;

  constructor(
    private produitService: ProduitsService,
    private sousCategorieService: SousCategorieService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.idProduit = this.activatedRoute.snapshot.paramMap.get('id');
    this.getProduitDetails();
  }

  getProduitDetails(): void {
    if (this.idProduit) {
      this.produitService.getProduit(this.idProduit).subscribe({
        next: (data) => {
          this.produit = data;
          if (this.produit && !Array.isArray(this.produit.categoryList)) {
            this.produit.categoryList = this.produit.categoryList ? [this.produit.categoryList] : [];
          }

          if (this.produit?.user?.availableLanguages && Array.isArray(this.produit.user.availableLanguages)) {
            this.agencyAvailableLanguages = this.produit.user.availableLanguages.map((l: string) => l.toUpperCase());
          } else {
            this.agencyAvailableLanguages = ['FR'];
          }

          this.showFrenchFields  = this.agencyAvailableLanguages.includes('FR');
          this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
          this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');

          this.cdr.detectChanges();
          // Re-sync les éditeurs après que les champs soient affichés
          this.applyRichTextAfterRender();
        },
        error: () => {
          Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de récupérer le produit', confirmButtonColor: '#f44336' });
        }
      });
    }
  }

  /* ============================================================
     ✅ SYNC DES ÉDITEURS contenteditable
     ============================================================ */
  private applyRichTextAfterRender(): void {
    setTimeout(() => this.syncEditors(), 0);
    setTimeout(() => this.syncEditors(), 100);
    setTimeout(() => this.syncEditors(), 300);
  }

  private syncEditors(): void {
    if (this.descFrEditor) {
      const html = this.sousCategorie.description || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descArEditor) {
      const html = this.sousCategorie.descriptionarabe || '';
      if (this.descArEditor.nativeElement.innerHTML !== html) {
        this.descArEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descEnEditor) {
      const html = this.sousCategorie.descriptionanglais || '';
      if (this.descEnEditor.nativeElement.innerHTML !== html) {
        this.descEnEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLERS (input) DES CONTENTEDITABLE
     ============================================================ */
  onDescFrInput(): void {
    if (this.descFrEditor) {
      this.sousCategorie.description = this.descFrEditor.nativeElement.innerHTML;
    }
  }

  onDescArInput(): void {
    if (this.descArEditor) {
      this.sousCategorie.descriptionarabe = this.descArEditor.nativeElement.innerHTML;
    }
  }

  onDescEnInput(): void {
    if (this.descEnEditor) {
      this.sousCategorie.descriptionanglais = this.descEnEditor.nativeElement.innerHTML;
    }
  }

  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // ==================== UTILITAIRE ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToSafeUrl(file: File): Promise<SafeUrl> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result;
        resolve(this.sanitizer.bypassSecurityTrustUrl(base64));
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: ImageUploadMode): void {
    if (this.uploadingMain) return;
    if (this.isAwaitingMainValidation) {
      this.cancelMainPreview(true);
    }
    this.mainUploadMode = mode;
    console.log('🔄 Mode image principale :', mode);
  }

  onMainFileSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    this.fileToSafeUrl(file).then(safeUrl => {
      this.mainPreviewOriginalUrl = safeUrl;
      this.cdr.detectChanges();
    });

    if (this.mainUploadMode === 'direct') {
      this.mainImage = { file, safeUrl: this.sanitizer.bypassSecurityTrustUrl('') };
      this.fileToSafeUrl(file).then(s => (this.mainImage!.safeUrl = s));
      this.mainPreviewOriginalUrl = null;
      this.cdr.detectChanges();
    } else {
      this.uploadMainViaImagesousproduit(file);
    }

    input.value = '';
  }

  private uploadMainViaImagesousproduit(file: File): void {
    this.uploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([file], 'sous-categories').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const rawUrl = response.urls[0];
          this.mainPreviewProcessedUrl = rawUrl.startsWith('http') ? rawUrl : this.s3BaseUrl + rawUrl;
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.mainPreviewOriginalUrl = null;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.uploadingMain = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.mainPreviewOriginalUrl = null;
        this.cdr.detectChanges();
      }
    });
  }

  validateMainPreview(): void {
    if (!this.mainPreviewProcessedUrl) return;

    this.mainImage = {
      file: new File([], 'main-image-validated.jpg', { type: 'image/jpeg' }),
      safeUrl: this.mainPreviewProcessedUrl as any,
      uploadedUrl: this.mainPreviewProcessedUrl
    };

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;

    Swal.fire({ icon: 'success', title: 'Image validée', text: 'L\'image principale a été acceptée.', timer: 1500, showConfirmButton: false });
    this.cdr.detectChanges();
  }

  cancelMainPreview(silent: boolean = false): void {
    const urlToDelete = this.mainPreviewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image principale refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
      this.cdr.detectChanges();
    };

    if (silent) { doCancel(); return; }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) {
      this.cancelMainPreview();
      return;
    }
    this.mainImage = null;
    this.cdr.detectChanges();
  }

  // ============================================================
  // ============ SECONDARY IMAGES (galerie) ====================
  // ============================================================

  setSecondaryUploadMode(mode: ImageUploadMode): void {
    if (this.uploadingSecondary) return;
    if (this.isAwaitingSecondaryValidation) {
      this.cancelSecondaryPreview(true);
    }
    this.secondaryUploadMode = mode;
    console.log('🔄 Mode images secondaires :', mode);
  }

  onSecondaryFilesSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (this.validateImageFile(files[i])) validFiles.push(files[i]);
    }
    if (validFiles.length === 0) { input.value = ''; return; }

    if (this.secondaryUploadMode === 'direct') {
      this.processSecondaryDirect(validFiles);
    } else {
      this.secondaryPreviewOriginalUrls = [];
      validFiles.forEach(f => {
        this.fileToSafeUrl(f).then(s => {
          this.secondaryPreviewOriginalUrls.push(s);
          this.cdr.detectChanges();
        });
      });
      this.uploadSecondaryViaImagesousproduit(validFiles);
    }

    input.value = '';
  }

  private processSecondaryDirect(files: File[]): void {
    files.forEach(file => {
      this.fileToSafeUrl(file).then(safeUrl => {
        this.secondaryImages.push({ file, safeUrl });
        this.cdr.detectChanges();
      });
    });
  }

  private uploadSecondaryViaImagesousproduit(files: File[]): void {
    this.uploadingSecondary = true;
    this.isAwaitingSecondaryValidation = false;
    this.secondaryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'sous-categories').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingSecondary = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.secondaryPreviewProcessedUrls = response.urls.map(u => u.startsWith('http') ? u : this.s3BaseUrl + u);
          this.isAwaitingSecondaryValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.secondaryPreviewOriginalUrls = [];
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.uploadingSecondary = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.secondaryPreviewOriginalUrls = [];
        this.cdr.detectChanges();
      }
    });
  }

  validateSecondaryPreview(): void {
    if (this.secondaryPreviewProcessedUrls.length === 0) return;

    this.secondaryPreviewProcessedUrls.forEach(url => {
      this.secondaryImages.push({
        file: new File([], 'secondary-validated.jpg', { type: 'image/jpeg' }),
        safeUrl: url as any,
        uploadedUrl: url
      });
    });

    this.secondaryPreviewOriginalUrls = [];
    this.secondaryPreviewProcessedUrls = [];
    this.isAwaitingSecondaryValidation = false;

    Swal.fire({ icon: 'success', title: 'Images validées', text: `${this.secondaryImages.length} image(s) dans la galerie.`, timer: 1500, showConfirmButton: false });
    this.cdr.detectChanges();
  }

  cancelSecondaryPreview(silent: boolean = false): void {
    const urlsToDelete = [...this.secondaryPreviewProcessedUrls];

    const doCancel = () => {
      urlsToDelete.forEach(url => {
        this.awsService.deleteFile(url).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      });
      this.secondaryPreviewOriginalUrls = [];
      this.secondaryPreviewProcessedUrls = [];
      this.isAwaitingSecondaryValidation = false;
      this.cdr.detectChanges();
    };

    if (silent) { doCancel(); return; }

    Swal.fire({
      title: 'Refuser ces images ?',
      text: 'Les images traitées seront supprimées et vous pourrez en choisir d\'autres.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeSecondaryImage(index: number): void {
    this.secondaryImages.splice(index, 1);
    this.cdr.detectChanges();
  }

  // ============================================================
  // ==================== UPLOAD & SUBMIT =======================
  // ============================================================

  private async uploadPendingImages(images: PendingImage[]): Promise<string[]> {
    const filesToUpload = images.filter(img => !img.uploadedUrl).map(img => img.file);

    if (filesToUpload.length === 0) {
      return images.map(img => img.uploadedUrl!).filter(url => url);
    }

    return new Promise((resolve, reject) => {
      this.awsService.uploadFiles(filesToUpload, 'sous-categories').subscribe({
        next: (response) => {
          if (response.success && response.urls?.length) {
            let idx = 0;
            for (let i = 0; i < images.length; i++) {
              if (!images[i].uploadedUrl) {
                const rawUrl = response.urls[idx++];
                const fullUrl = rawUrl.startsWith('http') ? rawUrl : this.s3BaseUrl + rawUrl;
                images[i].uploadedUrl = fullUrl;
              }
            }
            resolve(images.map(img => img.uploadedUrl!).filter(url => url));
          } else {
            reject(new Error(response.error || 'Upload failed'));
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  private validateForm(): boolean {
    if (this.isAwaitingMainValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser l\'image principale.', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (this.isAwaitingSecondaryValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser les images secondaires.', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  resetForm(): void {
    this.sousCategorie = new SousCategorie();
    this.mainImage = null;
    this.secondaryImages = [];

    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    this.mainUploadMode = 'direct';

    this.secondaryPreviewOriginalUrls = [];
    this.secondaryPreviewProcessedUrls = [];
    this.isAwaitingSecondaryValidation = false;
    this.secondaryUploadMode = 'direct';

    this.cdr.detectChanges();
    // Vider les éditeurs contenteditable
    this.applyRichTextAfterRender();
  }

  async onSubmit(): Promise<void> {
    if (!this.validateForm()) return;
    if (!this.idProduit) {
      Swal.fire({ icon: 'error', title: 'ID produit manquant', text: 'Impossible de créer', confirmButtonColor: '#f44336' });
      return;
    }

    // 🔥 Récupérer les valeurs depuis les éditeurs contenteditable
    this.onDescFrInput();
    this.onDescArInput();
    this.onDescEnInput();

    this.isSubmitting = true;
    Swal.fire({ title: 'Upload des images...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      this.uploadingMain = true;
      this.uploadingSecondary = true;

      let mainUrls: string[] = [];
      if (this.mainImage) {
        mainUrls = await this.uploadPendingImages([this.mainImage]);
      }
      const secondaryUrls = await this.uploadPendingImages(this.secondaryImages);

      this.uploadingMain = false;
      this.uploadingSecondary = false;

      const payload = {
        name: this.sousCategorie.name?.trim() || '',
        namearabe: this.sousCategorie.namearabe?.trim() || '',
        nameanglais: this.sousCategorie.nameanglais?.trim() || '',
        description: this.sousCategorie.description || '',
        descriptionarabe: this.sousCategorie.descriptionarabe || '',
        descriptionanglais: this.sousCategorie.descriptionanglais || '',
        category: this.sousCategorie.category?.trim() || '',
        prix: this.sousCategorie.prix || '',
        video: this.sousCategorie.video?.trim() || '',
        image_url: mainUrls.join(','),
        listimages: secondaryUrls
      };

      console.log('📦 Payload envoyé:', payload);

      Swal.update({ title: 'Création en cours...' });

      this.sousCategorieService.createSousCategorie(payload, this.idProduit).subscribe({
        next: () => {
          this.isSubmitting = false;
          Swal.close();
          Swal.fire({ icon: 'success', title: 'Succès !', text: 'Sous-catégorie créée', timer: 1500, showConfirmButton: false });
          this.goToSousList();
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting = false;
          Swal.close();
          let msg = 'Erreur lors de la création';
          if (error.error?.message) msg = error.error.message;
          Swal.fire({ icon: 'error', title: 'Erreur', text: msg, confirmButtonColor: '#f44336' });
        }
      });
    } catch (err) {
      this.uploadingMain = false;
      this.uploadingSecondary = false;
      this.isSubmitting = false;
      Swal.close();
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Erreur upload', text: 'Échec du téléchargement des images', confirmButtonColor: '#f44336' });
    }
  }

  cancel(): void {
    this.goToSousList();
  }

  private goToSousList(): void {
    if (this.idProduit) {
      this.router.navigate(['/list-sous-produits', this.idProduit]);
    } else {
      this.location.back();
    }
  }

  navigateBack(): void {
    this.cancel();
  }
}